import React from 'react';
import './App.css';
import logo from './fixed-point.png';

function App() {
  return (
    <div className="App">
      <header className="header">
        <nav className="nav">
          <div className="nav-brand">
            <img src={logo} alt="Fixed Point Logo" className="nav-logo" />
            <span>Fixed Point AI</span>
          </div>
          <div className="nav-links">
            <a href="#services">Services</a>
            <a href="#work">Work</a>
            <a href="#experience">Experience</a>
            <a href="#contact">Contact</a>
          </div>
        </nav>
      </header>

      <main>
        <section className="hero">
          <div className="container">
            <div className="hero-badge">AI & Data Science Consulting</div>
            <h1 className="hero-title">Fixed Point AI</h1>
            <p className="hero-description">
              We deliver production-grade AI, machine learning, and optimization solutions
              that drive measurable business outcomes. From recommendation engines to marketing
              science, we turn complex data challenges into competitive advantages.
            </p>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">15+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat">
                <span className="stat-number">50+</span>
                <span className="stat-label">Projects Delivered</span>
              </div>
              <div className="stat">
                <span className="stat-number">5</span>
                <span className="stat-label">Industries Served</span>
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="services">
          <div className="container">
            <div className="section-header">
              <h2>Our Services</h2>
              <p className="section-subtitle">End-to-end solutions from strategy to deployment</p>
            </div>
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <h3>Machine Learning & AI</h3>
                <p>
                  Custom ML models, LLM integration, recommendation systems, and predictive
                  analytics tailored to your business objectives.
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6m0 6v10M1 12h6m6 0h10"/>
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                </div>
                <h3>Optimization & Simulation</h3>
                <p>
                  Large-scale optimization for routing, scheduling, and resource allocation.
                  Discrete-event simulation for operational planning.
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3v18h18"/>
                    <path d="M18 17V9"/>
                    <path d="M13 17V5"/>
                    <path d="M8 17v-3"/>
                  </svg>
                </div>
                <h3>Marketing Science</h3>
                <p>
                  Media mix modeling (MMM), multi-touch attribution (MTA), triangulation,
                  and customer lifetime value modeling.
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                  </svg>
                </div>
                <h3>Data Engineering</h3>
                <p>
                  Scalable data pipelines, RAG architectures, knowledge graphs, and
                  production ML infrastructure.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="work" className="work">
          <div className="container">
            <div className="section-header">
              <h2>Featured Work</h2>
              <p className="section-subtitle">Selected projects across industries</p>
            </div>
            <div className="work-grid">
              <div className="work-card">
                <div className="work-category">Financial Services</div>
                <h3>Recommendation Engine</h3>
                <p>
                  Built a personalized product recommendation engine for a major financial
                  services company, improving conversion rates and customer engagement through
                  ML-driven targeting.
                </p>
                <div className="work-tags">
                  <span>Machine Learning</span>
                  <span>Personalization</span>
                  <span>Python</span>
                </div>
              </div>

              <div className="work-card">
                <div className="work-category">Executive Coaching</div>
                <h3>AI Data Foundation & Agentic Solutions</h3>
                <p>
                  Developed an AI-powered data foundation and agentic AI solutions for an
                  executive coaching platform, enabling intelligent insights and automated
                  workflows for coaching professionals.
                </p>
                <div className="work-tags">
                  <span>LLM</span>
                  <span>Agentic AI</span>
                  <span>Data Architecture</span>
                </div>
              </div>

              <div className="work-card">
                <div className="work-category">Productivity</div>
                <h3>Markdown Document Management</h3>
                <p>
                  Created a full-featured web application for markdown document management,
                  providing intuitive editing, organization, and collaboration capabilities
                  for content teams.
                </p>
                <div className="work-tags">
                  <span>React</span>
                  <span>Full Stack</span>
                  <span>UX Design</span>
                </div>
              </div>

              <div className="work-card">
                <div className="work-category">Marketing Analytics</div>
                <h3>Marketing Science Suite</h3>
                <p>
                  Implemented comprehensive marketing measurement solutions including media
                  mix modeling (MMM), multi-touch attribution (MTA), and triangulation
                  methodologies to optimize marketing spend.
                </p>
                <div className="work-tags">
                  <span>MMM</span>
                  <span>Attribution</span>
                  <span>Bayesian Methods</span>
                </div>
              </div>

              <div className="work-card">
                <div className="work-category">Customer Analytics</div>
                <h3>LTV & Loyalty Economics</h3>
                <p>
                  Developed customer lifetime value models and loyalty economy frameworks,
                  enabling data-driven customer segmentation and retention strategy optimization.
                </p>
                <div className="work-tags">
                  <span>LTV Modeling</span>
                  <span>Segmentation</span>
                  <span>Predictive Analytics</span>
                </div>
              </div>

              </div>
          </div>
        </section>

        <section id="experience" className="experience">
          <div className="container">
            <div className="section-header">
              <h2>Experience & Expertise</h2>
              <p className="section-subtitle">Trusted by leading enterprises across industries</p>
            </div>

            <div className="clients-showcase">
              <div className="client-logo">American Express</div>
              <div className="client-logo">Discover</div>
              <div className="client-logo">Morgan Stanley</div>
              <div className="client-logo">Ascensus</div>
              <div className="client-logo">Noom</div>
              <div className="client-logo">Major Airlines</div>
            </div>

            <div className="expertise-areas">
              <h3>Technical Expertise</h3>
              <div className="expertise-tags">
                <span className="tag">Large Language Models</span>
                <span className="tag">Retrieval-Augmented Generation</span>
                <span className="tag">Recommendation Systems</span>
                <span className="tag">Customer Lifetime Value</span>
                <span className="tag">Media Mix Modeling</span>
                <span className="tag">Multi-Touch Attribution</span>
                <span className="tag">Mathematical Optimization</span>
                <span className="tag">Discrete-Event Simulation</span>
                                <span className="tag">Knowledge Graphs</span>
                <span className="tag">NLP & Conversational AI</span>
                <span className="tag">Revenue Management</span>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="contact">
          <div className="container">
            <h2>Let's Work Together</h2>
            <p>
              Ready to transform your data into actionable intelligence?
              We'd love to discuss how we can help solve your most challenging problems.
            </p>
            <a href="mailto:billpun@fixedpointai.com" className="contact-btn">Get in Touch</a>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={logo} alt="Fixed Point Logo" className="footer-logo" />
              <span>Fixed Point AI</span>
            </div>
            <p>&copy; 2025 Fixed Point LLC. AI consulting and software development.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
